import Phaser from 'phaser'
import { Capacitor } from '@capacitor/core'
import { SettingsManager } from '../game/SettingsManager'
import { FontConfig, createTextStyle } from '../config/UIConfig'
import { GameCenter } from '../native/GameCenter'
import { Analytics } from '../utils/Analytics'
import { GameStateSaver } from '../utils/GameStateSaver'
import { ScreenshotMode } from '../utils/ScreenshotMode'
import { ResponsiveLayout } from '../utils/ResponsiveLayout'

export default class Title extends Phaser.Scene {
  private startButton!: Phaser.GameObjects.Text;
  private continueButton?: Phaser.GameObjects.Text;
  private settingsManager: SettingsManager
  private menuButtons: Phaser.GameObjects.Container[] = []
  private titleText!: Phaser.GameObjects.Text
  private gameCenterButtons: Phaser.GameObjects.GameObject[] = []

  constructor() {
    super('Title')
    this.settingsManager = SettingsManager.getInstance()
  }

  async create() {
    console.log('Title scene: create() method started')

    // Initialize ScreenshotMode
    await ScreenshotMode.initialize()

    // Check if we should auto-navigate to game for screenshots
    if (ScreenshotMode.shouldAutoNavigate()) {
      console.log('📸 Screenshot mode detected - auto-navigating to game')
      // Skip title screen and go straight to game
      this.time.delayedCall(500, () => {
        this.scene.start('ModeSelection')
      })
      return // Don't create title screen UI
    }

    // Check if there's a saved game AND the app was recently backgrounded
    // If so, auto-resume instead of showing title screen
    const hasSavedGame = GameStateSaver.hasSavedGame()
    if (hasSavedGame) {
      const wasRecentlyBackgrounded = this.checkIfRecentlyBackgrounded()
      const wasManualQuit = localStorage.getItem('manualQuit') === 'true'

      // Only auto-resume if recently backgrounded AND not a manual quit
      if (wasRecentlyBackgrounded && !wasManualQuit) {
        console.log('Title scene: Auto-resuming saved game (app was recently backgrounded)')
        // Signal that music should start when Game scene loads
        this.registry.set('startMusic', true)
        this.scene.start('Game', { loadSavedGame: true })
        return // Skip showing title screen
      }

      // Note: Don't clear manualQuit flag here - it should persist until user takes action
      // The flag will be cleared when user starts a new game or loads the saved game
    }

    // Track screen view
    Analytics.trackScreen('Title')

    this.createUI()

    // Add resize handler
    this.scale.on('resize', this.handleResize, this)

    // Add keyboard support
    console.log('Title scene: Adding keyboard support')
    this.input.keyboard!.on('keydown-ENTER', () => {
      this.scene.start('Game')
    })

    this.input.keyboard!.on('keydown-SPACE', () => {
      this.scene.start('Game')
    })

    // Add about keyboard shortcut
    this.input.keyboard!.on('keydown-I', () => {
      this.scene.start('About', { returnTo: 'Title' })
    })

    // Add help keyboard shortcut
    this.input.keyboard!.on('keydown-H', () => {
      this.scene.start('Help')
    })

    // Add settings keyboard shortcut
    this.input.keyboard!.on('keydown-S', () => {
      this.scene.start('Settings', { returnTo: 'Title' })
    })

    console.log('Title scene: create() method completed successfully')
  }

  private createUI(): void {
    const w = this.scale.width
    const h = this.scale.height
    const responsive = ResponsiveLayout.getConfig(this)
    const margins = ResponsiveLayout.getMargins(this)

    console.log('Title scene: Screen dimensions:', w, 'x', h, 'Breakpoint:', responsive.breakpoint)

    // Background - use selected background from settings
    console.log('Title scene: Adding background image')
    const backgroundSetting = this.settingsManager.getBackgroundImage()
    const backgroundKey = backgroundSetting === 'default' ? 'bg' : `bg-${backgroundSetting}`
    const bg = this.add.image(w / 2, h / 2, backgroundKey)
    // Scale to cover entire screen, cropping excess to avoid black bars
    const bgWidth = this.registry.get('bgWidth') || 1080
    const bgHeight = this.registry.get('bgHeight') || 1920
    const scaleX = w / bgWidth
    const scaleY = h / bgHeight
    const scale = Math.max(scaleX, scaleY)
    bg.setScale(scale)

    // Title - responsive font size and position
    console.log('Title scene: Adding title text')
    const titleFontSize = ResponsiveLayout.getFontSize(this, 64)
    const titleY = responsive.isPortrait ? h * 0.15 : h * 0.22 + 30
    this.titleText = this.add.text(w / 2, titleY, 'Cell Swap!',
      createTextStyle('title', { fontSize: `${titleFontSize}px`, color: '#6e2d55' })
    ).setOrigin(0.5)

    // Get saved game status
    const hasSavedGame = GameStateSaver.hasSavedGame()

    // Helper to create consistent menu buttons
    const buttonSize = ResponsiveLayout.getButtonSize(this, 200, 56)
    const createMenuButton = (
      y: number,
      label: string,
      baseColor: number | string,
      hoverColor: number | string,
      onClick: () => void,
      customFontSize?: number
    ) => {
      // Container to hold rect + text (prevents nested-looking text backgrounds)
      const container = this.add.container(w / 2, y)

      const rect = this.add.rectangle(0, 0, buttonSize.width, buttonSize.height, typeof baseColor === 'string' ? parseInt(baseColor.replace('#', ''), 16) : baseColor, 1)
      rect.setInteractive({ useHandCursor: true })
      rect.setStrokeStyle(2, 0xffffff, 0.15)

      const buttonFontSize = customFontSize ? ResponsiveLayout.getFontSize(this, customFontSize) : ResponsiveLayout.getFontSize(this, 24)
      const text = this.add.text(0, 0, label,
        createTextStyle('button', { fontSize: `${buttonFontSize}px`, color: '#ffffff' })
      ).setOrigin(0.5)

      container.add([rect, text])
      this.menuButtons.push(container)

      const enterHover = () => {
        rect.setFillStyle(typeof hoverColor === 'string' ? parseInt(hoverColor.replace('#', ''), 16) : hoverColor)
        rect.setScale(1.03)
      }
      const exitHover = () => {
        rect.setFillStyle(typeof baseColor === 'string' ? parseInt(baseColor.replace('#', ''), 16) : baseColor)
        rect.setScale(1)
      }

      rect.on('pointerover', enterHover)
      rect.on('pointerout', exitHover)
      rect.on('pointerup', () => {
        onClick()
      })

      return text // Return text object for startButton reference (still centered within container)
    }

    // Start button (now matches the other buttons' structure)
    console.log('Title scene: Adding start button (standardized)')
    // Unified monochromatic palette derived from background theme (pink/plum spectrum)
    // Chosen to provide visual hierarchy while keeping consistent mood.
    const buttonPalette = {
      continue: { base: 0xe89c39, hover: 0xf2af4f },  // gold for continue (priority)
      start: { base: 0xc8618d, hover: 0xd7769e },     // light accent
      about: { base: 0xb75081, hover: 0xc26593 },     // mid accent
      help: { base: 0xa04171, hover: 0xb15582 },      // deeper shade
      settings: { base: 0x8a3662, hover: 0x9b4571 }   // darkest for lower priority
    }

    // Calculate button layout based on whether Continue button is present and screen size
    const numButtons = hasSavedGame ? 5 : 4
    const baseSpacing = responsive.isPortrait ? h * 0.12 : h * 0.11
    const buttonSpacing = Math.min(baseSpacing, (h * 0.7) / numButtons) // Ensure buttons fit
    const startY = responsive.isPortrait ? h * 0.35 : h * 0.45 // Higher start in portrait
    let currentY = startY

    if (hasSavedGame) {
      console.log('Title scene: Adding continue button (saved game found)')
      // Use smaller font on web only
      const continueFontSize = Capacitor.isNativePlatform() ? 24 : 20
      this.continueButton = createMenuButton(currentY, 'Continue Game', buttonPalette.continue.base, buttonPalette.continue.hover, () => {
        console.log('Continue button clicked - loading saved game')
        // Clear the manual quit flag since user is explicitly continuing
        localStorage.removeItem('manualQuit')
        // Signal that music should start when Game scene loads
        this.registry.set('startMusic', true)
        this.scene.start('Game', { loadSavedGame: true })
      }, continueFontSize)
      currentY += buttonSpacing
    }

    this.startButton = createMenuButton(currentY, 'New Game', buttonPalette.start.base, buttonPalette.start.hover, () => {
      // Clear the manual quit flag since user is starting a new game
      localStorage.removeItem('manualQuit')
      // Signal that music should start when Game scene eventually loads
      this.registry.set('startMusic', true)
      this.scene.start('ModeSelection')
    })
    currentY += buttonSpacing

    console.log('Title scene: Adding about button')
    const aboutBtn = createMenuButton(currentY, 'About', buttonPalette.about.base, buttonPalette.about.hover, () => {
      this.scene.start('About', { returnTo: 'Title' })
    })
    currentY += buttonSpacing

    console.log('Title scene: Adding help button')
    const helpBtn = createMenuButton(currentY, 'Help', buttonPalette.help.base, buttonPalette.help.hover, () => {
      this.scene.start('Help')
    })
    currentY += buttonSpacing

    console.log('Title scene: Adding settings button')
    const settingsBtn = createMenuButton(currentY, 'Settings', buttonPalette.settings.base, buttonPalette.settings.hover, () => {
      this.scene.start('Settings', { returnTo: 'Title' })
    })

    // Add Game Center buttons (top-right corner)
    this.createGameCenterButtons(w, h)
  }

  /**
   * Create Game Center buttons for leaderboards and achievements
   */
  private createGameCenterButtons(w: number, h: number): void {
    // Clear existing buttons
    this.gameCenterButtons.forEach(btn => btn.destroy())
    this.gameCenterButtons = []

    // Always show Game Center buttons (they'll show a message if not available)
    const isAvailable = GameCenter.isGameCenterAvailable()
    console.log('Creating Game Center buttons, available:', isAvailable)

    const responsive = ResponsiveLayout.getConfig(this)
    const margins = ResponsiveLayout.getMargins(this)
    const safeArea = ResponsiveLayout.getSafeArea()

    const buttonSize = responsive.isPortrait ? 45 : 50
    const padding = responsive.isPortrait ? 10 : 15
    const bottomMargin = Math.max(20, safeArea.bottom) + margins.vertical
    const rightMargin = Math.max(padding, safeArea.right) + margins.horizontal

    // Helper to create icon buttons
    const createIconButton = (
      x: number,
      y: number,
      icon: string,
      label: string,
      onClick: () => void
    ) => {
      const button = this.add.rectangle(x, y, buttonSize, buttonSize, 0x8a3662, 0.8)
      button.setInteractive({ useHandCursor: true })
      button.setStrokeStyle(2, 0xffffff, 0.3)
      this.gameCenterButtons.push(button)

      const fontSize = ResponsiveLayout.getFontSize(this, 24)
      const text = this.add.text(x, y, icon, {
        fontSize: `${fontSize}px`,
        color: '#ffffff'
      }).setOrigin(0.5)
      this.gameCenterButtons.push(text)

      // Tooltip
      const tooltipFontSize = ResponsiveLayout.getFontSize(this, 12)
      const tooltip = this.add.text(x, y + buttonSize / 2 + 20, label,
        createTextStyle('label', {
          fontSize: `${tooltipFontSize}px`,
          backgroundColor: '#000000',
          padding: { x: 6, y: 4 }
        })
      ).setOrigin(0.5).setVisible(false).setDepth(1000)
      this.gameCenterButtons.push(tooltip)

      button.on('pointerover', () => {
        button.setFillStyle(0x9b4571)
        button.setScale(1.05)
        tooltip.setVisible(true)
      })

      button.on('pointerout', () => {
        button.setFillStyle(0x8a3662)
        button.setScale(1)
        tooltip.setVisible(false)
      })

      button.on('pointerup', onClick)
    }

    // Leaderboard button (trophy icon)
    createIconButton(
      w - padding - buttonSize / 2 - buttonSize - padding,
      h - bottomMargin - buttonSize / 2,
      '🏆',
      'Leaderboards',
      () => {
        if (!isAvailable) {
          this.showNotSupportedDialog('Game Center is not available. This feature requires an iOS device with Game Center enabled.')
          return
        }
        console.log('Opening Game Center leaderboards')
        GameCenter.showLeaderboard()
      }
    )

    // Achievements button (star icon)
    createIconButton(
      w - padding - buttonSize / 2,
      h - bottomMargin - buttonSize / 2,
      '⭐',
      'Achievements',
      () => {
        if (!isAvailable) {
          this.showNotSupportedDialog('Game Center is not available. This feature requires an iOS device with Game Center enabled.')
          return
        }
        console.log('Opening Game Center achievements')
        GameCenter.showAchievements()
      }
    )

    // Show authentication status (small indicator)
    const statusText = isAvailable
      ? (GameCenter.isPlayerAuthenticated() ? '✓ Connected' : '○ Sign In')
      : '○ Not Available'

    const authStatus = this.add.text(
      w - padding - buttonSize - padding - buttonSize / 2,
      h - bottomMargin - buttonSize - 10,
      statusText,
      createTextStyle('label', {
        fontSize: '10px',
        color: isAvailable && GameCenter.isPlayerAuthenticated() ? '#4ade80' : '#94a3b8'
      })
    ).setOrigin(0.5)

    // If not available yet, wait for initialization and update status
    if (!isAvailable) {
      // Start polling for Game Center availability
      const pollInterval = this.time.addEvent({
        delay: 1000, // Check every second
        callback: () => {
          const nowAvailable = GameCenter.isGameCenterAvailable()
          if (nowAvailable) {
            console.log('Game Center became available!')
            // Stop polling
            pollInterval.destroy()

            // Update status
            const newStatusText = GameCenter.isPlayerAuthenticated() ? '✓ Connected' : '○ Sign In'
            authStatus.setText(newStatusText)
            authStatus.setColor(GameCenter.isPlayerAuthenticated() ? '#4ade80' : '#94a3b8')

            // If authenticated, we're done
            if (GameCenter.isPlayerAuthenticated()) {
              return
            }

            // Otherwise allow clicking to authenticate
            authStatus.setInteractive({ useHandCursor: true })
            authStatus.on('pointerup', async () => {
              console.log('Attempting Game Center authentication...')
              const success = await GameCenter.authenticatePlayer()
              if (success) {
                authStatus.setText('✓ Connected')
                authStatus.setColor('#4ade80')
                authStatus.setInteractive(false)
              }
            })
          }
        },
        loop: true
      })

      // Stop polling after 10 seconds to avoid infinite checking
      this.time.delayedCall(10000, () => {
        if (pollInterval.paused === false) {
          console.log('Game Center polling timed out after 10 seconds')
          pollInterval.destroy()
        }
      })
    } else if (!GameCenter.isPlayerAuthenticated()) {
      // Available but not authenticated - allow clicking to authenticate
      authStatus.setInteractive({ useHandCursor: true })
      authStatus.on('pointerup', async () => {
        console.log('Attempting Game Center authentication...')
        const success = await GameCenter.authenticatePlayer()
        if (success) {
          authStatus.setText('✓ Connected')
          authStatus.setColor('#4ade80')
          authStatus.setInteractive(false)
        }
      })
    }
  }

  /**
   * Show a dialog when Game Center is not supported
   */
  private showNotSupportedDialog(message: string): void {
    const w = this.scale.width
    const h = this.scale.height

    // Create overlay
    const overlay = this.add.rectangle(w / 2, h / 2, w, h, 0x000000, 0.7)
    overlay.setInteractive()
    overlay.setDepth(9999)

    // Create dialog
    const dialogWidth = Math.min(400, w - 40)
    const dialogHeight = 200
    const dialogBg = this.add.rectangle(w / 2, h / 2, dialogWidth, dialogHeight, 0x2c3e50, 1)
    dialogBg.setStrokeStyle(3, 0xffffff, 0.8)
    dialogBg.setDepth(10000)

    // Title
    const title = this.add.text(w / 2, h / 2 - 50, 'Not Available',
      createTextStyle('title', {
        fontSize: '24px',
        color: '#e74c3c'
      })
    ).setOrigin(0.5).setDepth(10001)

    // Message
    const messageText = this.add.text(w / 2, h / 2, message,
      createTextStyle('label', {
        fontSize: '14px',
        color: '#ffffff',
        wordWrap: { width: dialogWidth - 40 },
        align: 'center'
      })
    ).setOrigin(0.5).setDepth(10001)

    // OK button
    const okButton = this.add.text(w / 2, h / 2 + 60, 'OK', {
      fontSize: '20px',
      color: '#ffffff',
      backgroundColor: '#3498db',
      padding: { x: 30, y: 10 },
      fontFamily: 'Arial, sans-serif'
    })
      .setOrigin(0.5)
      .setDepth(10002)
      .setInteractive({ useHandCursor: true })

    okButton.on('pointerover', () => {
      okButton.setStyle({ backgroundColor: '#5dade2' })
    })
    okButton.on('pointerout', () => {
      okButton.setStyle({ backgroundColor: '#3498db' })
    })
    okButton.on('pointerup', () => {
      // Clean up dialog
      overlay.destroy()
      dialogBg.destroy()
      title.destroy()
      messageText.destroy()
      okButton.destroy()
    })
  }

  /**
   * Check if the app was recently backgrounded (within last 5 minutes)
   */
  private checkIfRecentlyBackgrounded(): boolean {
    const BACKGROUND_THRESHOLD = 5 * 60 * 1000 // 5 minutes

    try {
      const lastSaveTime = localStorage.getItem('lastGameSaveTime')
      if (!lastSaveTime) {
        return false
      }

      const lastSave = parseInt(lastSaveTime, 10)
      const now = Date.now()
      const timeSinceLastSave = now - lastSave

      // If game was saved within last 5 minutes, likely the app was just backgrounded
      return timeSinceLastSave < BACKGROUND_THRESHOLD
    } catch (error) {
      console.warn('Failed to check background timestamp:', error)
      return false
    }
  }

  private handleResize(gameSize: Phaser.Structs.Size): void {
    // Clear all UI elements
    this.children.removeAll(true)
    this.menuButtons = []
    this.gameCenterButtons = []

    // Recreate UI with new dimensions
    this.createUI()
  }

  shutdown() {
    // Clean up resize listener
    this.scale.off('resize', this.handleResize, this)
  }
}

